import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Calculator, Check, Download, Info, Loader2, Pencil, X } from 'lucide-react';
import { toast } from 'sonner';
import type { LeaveRegisterRow } from '@ip/shared-types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useHrApi, SessionExpiredError } from '@/services/api';
import { downloadCsv, formatDateMedium, formatDays, formatDaysHours } from '@/lib/hr';
import { t } from '@/i18n';
import { EDUCATION_CREDITS, suggestedSeniority, type EducationKey } from './educationCredits';

/**
 * Art. 155 staż calculator: education level (single most favourable credit —
 * the statute forbids summing) + documented employment years OUTSIDE the
 * study period → suggested staż, applied into the seniority input.
 */
function SeniorityCalculator({ onApply }: { onApply: (years: number) => void }) {
  const [open, setOpen] = useState(false);
  const [education, setEducation] = useState<EducationKey>('none');
  const [employment, setEmployment] = useState('');

  const suggestion = suggestedSeniority(education, Number(employment.replace(',', '.')));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="text-muted-foreground size-8"
          aria-label={t('hr.register.calc.open')}
        >
          <Calculator className="size-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 space-y-3">
        <p className="text-sm font-medium">{t('hr.register.calc.title')}</p>
        <div className="space-y-1.5">
          <Label>{t('hr.register.calc.education')}</Label>
          <Select value={education} onValueChange={(v) => setEducation(v as EducationKey)}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EDUCATION_CREDITS.map((e) => (
                <SelectItem key={e.key} value={e.key}>
                  {t(`hr.register.calc.edu.${e.key}`)}{e.years > 0 ? ` (+${e.years})` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="calc-employment">{t('hr.register.calc.employment')}</Label>
          <Input
            id="calc-employment"
            inputMode="numeric"
            value={employment}
            placeholder="0"
            onChange={(e) => setEmployment(e.target.value)}
          />
        </div>
        <p className="text-muted-foreground text-xs">{t('hr.register.calc.rules')}</p>
        <div className="flex items-center justify-between">
          <span className="text-sm">
            {t('hr.register.calc.suggestion')}:{' '}
            <span className="font-semibold tabular-nums">{suggestion} {t('hr.register.calc.years')}</span>
            <span className="text-muted-foreground ml-1 text-xs">
              ({suggestion >= 10 ? '26' : '20'} {t('hr.register.calc.daysSuffix')})
            </span>
          </span>
          <Button
            size="sm"
            onClick={() => {
              onApply(suggestion);
              setOpen(false);
            }}
          >
            {t('hr.register.calc.apply')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface EditState {
  fte: string;
  seniority: string;
  adjustment: string;
  firstJob: boolean;
}

/**
 * Ewidencja urlopowa: per-employee etat / staż / wymiar / wykorzystano /
 * oczekuje / pozostało / zaległy with inline editing of the entitlement
 * inputs (fte, staż, first-job flag) and the year's manual adjustment.
 */
export function EntitlementTable({ nip }: { nip: string }) {
  const { leaveRegister, updateLeaveConfig } = useHrApi();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [rows, setRows] = useState<LeaveRegisterRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [edit, setEdit] = useState<EditState>({ fte: '1', seniority: '', adjustment: '0', firstJob: false });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (y: number) => {
    setLoading(true);
    try {
      setRows(await leaveRegister(nip, y));
    } catch (error) {
      if (!(error instanceof SessionExpiredError)) toast.error(t('hr.common.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [leaveRegister, nip]);

  useEffect(() => { load(year); }, [load, year]);

  const startEdit = (row: LeaveRegisterRow) => {
    setEditingId(row.employeeId);
    setEdit({
      fte: String(row.fte),
      seniority: row.leaveSeniorityYears === null ? '' : String(row.leaveSeniorityYears),
      adjustment: String(row.balance?.adjustmentDays ?? 0),
      firstJob: row.firstJobEver,
    });
  };

  const save = async (row: LeaveRegisterRow) => {
    const fte = Number(edit.fte.replace(',', '.'));
    const adjustment = Number(edit.adjustment.replace(',', '.'));
    const seniority = edit.seniority.trim() === '' ? null : Number(edit.seniority);
    if (!Number.isFinite(fte) || fte <= 0 || fte > 1) return;
    if (seniority !== null && (!Number.isInteger(seniority) || seniority < 0)) return;
    if (!Number.isFinite(adjustment)) return;
    setSaving(true);
    try {
      const updated = await updateLeaveConfig(nip, row.employeeId, {
        year,
        fte,
        leaveSeniorityYears: seniority,
        firstJobEver: edit.firstJob,
        adjustmentDays: adjustment,
      });
      setRows((rs) => rs.map((r) => (r.employeeId === row.employeeId ? updated : r)));
      setEditingId(null);
      toast.success(t('hr.register.saved'));
    } catch (error) {
      if (!(error instanceof SessionExpiredError)) {
        toast.error(error instanceof Error ? error.message : String(error));
      }
    } finally {
      setSaving(false);
    }
  };

  const exportCsv = () => {
    downloadCsv(`ewidencja-urlopowa-${year}.csv`, [
      [
        t('hr.register.employee'), t('hr.register.contract'), t('hr.register.fte'),
        t('hr.register.seniority'), t('hr.register.entitlement'), t('hr.register.carryOver'),
        t('hr.register.adjustment'), t('hr.register.used'), t('hr.register.pendingCol'),
        t('hr.register.remaining'), t('hr.register.overdue'),
      ],
      ...rows.map((r) => [
        `${r.lastName} ${r.firstName}`,
        r.employmentType ? t(`employees.type.${r.employmentType}`) : '',
        r.fte,
        r.leaveSeniorityYears ?? '',
        r.balance?.entitlementDays ?? '',
        r.balance?.carriedOverDays ?? '',
        r.balance?.adjustmentDays ?? '',
        r.balance?.usedDays ?? '',
        r.balance?.pendingDays ?? '',
        r.balance?.remainingDays ?? '',
        r.balance?.zalegly.days ?? '',
      ]),
    ]);
  };

  const years = [currentYear - 1, currentYear, currentYear + 1];

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="w-28" aria-label={t('hr.register.year')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="text-muted-foreground size-8" aria-label={t('hr.register.seniority')}>
                  <Info className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="max-w-72">{t('hr.register.seniorityHelp')}</TooltipContent>
            </Tooltip>
          </div>
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={rows.length === 0}>
            <Download className="size-4" />
            {t('hr.register.exportCsv')}
          </Button>
        </div>

        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 6 }, (_, i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : rows.length === 0 ? (
          <p className="text-muted-foreground py-8 text-center text-sm">{t('hr.register.empty')}</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('hr.register.employee')}</TableHead>
                  <TableHead>{t('hr.register.contract')}</TableHead>
                  <TableHead className="text-right">{t('hr.register.fte')}</TableHead>
                  <TableHead className="text-right">{t('hr.register.seniority')}</TableHead>
                  <TableHead className="text-right">{t('hr.register.entitlement')}</TableHead>
                  <TableHead className="text-right">{t('hr.register.adjustment')}</TableHead>
                  <TableHead className="text-right">{t('hr.register.used')}</TableHead>
                  <TableHead className="text-right">{t('hr.register.pendingCol')}</TableHead>
                  <TableHead className="text-right">{t('hr.register.remaining')}</TableHead>
                  <TableHead className="text-right">{t('hr.register.overdue')}</TableHead>
                  <TableHead className="w-20" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => {
                  const isUop = row.employmentType === 'employment_contract';
                  const editing = editingId === row.employeeId;
                  const b = row.balance;
                  return (
                    <TableRow key={row.employeeId}>
                      <TableCell className="whitespace-nowrap">
                        <div className="font-medium">{row.lastName} {row.firstName}</div>
                        {row.department && <div className="text-muted-foreground text-xs">{row.department}</div>}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-normal whitespace-nowrap">
                          {row.employmentType ? t(`employees.type.${row.employmentType}`) : t('employees.type.notSet')}
                        </Badge>
                      </TableCell>

                      {editing ? (
                        <>
                          <TableCell className="text-right">
                            <Input
                              className="ml-auto h-8 w-20 text-right"
                              inputMode="decimal"
                              value={edit.fte}
                              onChange={(e) => setEdit((s) => ({ ...s, fte: e.target.value }))}
                              aria-label={t('hr.register.fte')}
                            />
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-0.5">
                              <SeniorityCalculator
                                onApply={(years) => setEdit((s) => ({ ...s, seniority: String(years) }))}
                              />
                              <Input
                                className="h-8 w-16 text-right"
                                inputMode="numeric"
                                value={edit.seniority}
                                placeholder="—"
                                onChange={(e) => setEdit((s) => ({ ...s, seniority: e.target.value }))}
                                aria-label={t('hr.register.seniority')}
                              />
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <label className="flex items-center justify-end gap-1.5 text-xs whitespace-nowrap">
                              <Checkbox
                                checked={edit.firstJob}
                                onCheckedChange={(v) => setEdit((s) => ({ ...s, firstJob: v === true }))}
                              />
                              {t('hr.register.firstJobShort')}
                            </label>
                          </TableCell>
                          <TableCell className="text-right">
                            <Input
                              className="ml-auto h-8 w-20 text-right"
                              inputMode="decimal"
                              value={edit.adjustment}
                              onChange={(e) => setEdit((s) => ({ ...s, adjustment: e.target.value }))}
                              aria-label={t('hr.register.adjustment')}
                            />
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell className="text-right tabular-nums">{isUop ? row.fte : '—'}</TableCell>
                          <TableCell className="text-right tabular-nums">
                            {isUop
                              ? row.leaveSeniorityYears ?? <span className="text-amber-600 dark:text-amber-400">{t('hr.register.notConfigured')}</span>
                              : '—'}
                          </TableCell>
                          <TableCell className="text-right tabular-nums whitespace-nowrap">
                            {b ? (
                              <>
                                {formatDays(b.entitlementDays)}
                                {b.carriedOverDays > 0 && (
                                  <span className="text-muted-foreground"> +{formatDays(b.carriedOverDays)}</span>
                                )}
                                {row.firstJobEver && (
                                  <span className="text-muted-foreground text-xs"> · {t('hr.register.firstJobShort')}</span>
                                )}
                              </>
                            ) : (
                              <span className="text-muted-foreground">{t('hr.register.noPool')}</span>
                            )}
                          </TableCell>
                          <TableCell className={cn('text-right tabular-nums', (b?.adjustmentDays ?? 0) !== 0 && 'font-medium')}>
                            {b ? (b.adjustmentDays === 0 ? '—' : formatDays(b.adjustmentDays)) : '—'}
                          </TableCell>
                        </>
                      )}

                      <TableCell className="text-right tabular-nums">{b ? formatDays(b.usedDays) : '—'}</TableCell>
                      <TableCell className="text-right tabular-nums">{b ? formatDays(b.pendingDays) : '—'}</TableCell>
                      <TableCell className="text-right font-semibold tabular-nums whitespace-nowrap">
                        {b ? formatDaysHours(b.remainingHours, row.dailyNormHours) : '—'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums whitespace-nowrap">
                        {b && b.zalegly.days > 0 ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                                <AlertTriangle className="size-3.5" />
                                {formatDays(b.zalegly.days)}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              {t('hr.register.overdueBy', { deadline: formatDateMedium(b.zalegly.deadline) })}
                            </TooltipContent>
                          </Tooltip>
                        ) : b ? '—' : '—'}
                      </TableCell>

                      <TableCell>
                        {editing ? (
                          <div className="flex justify-end gap-1">
                            <Button size="icon" className="size-7" disabled={saving} onClick={() => save(row)} aria-label={t('hr.register.save')}>
                              {saving ? <Loader2 className="size-3.5 animate-spin" /> : <Check className="size-3.5" />}
                            </Button>
                            <Button size="icon" variant="ghost" className="size-7" disabled={saving} onClick={() => setEditingId(null)} aria-label={t('hr.register.cancel')}>
                              <X className="size-3.5" />
                            </Button>
                          </div>
                        ) : isUop ? (
                          <div className="flex justify-end">
                            <Button
                              size="icon"
                              variant="ghost"
                              className="text-muted-foreground size-7"
                              onClick={() => startEdit(row)}
                              aria-label={t('hr.register.edit')}
                            >
                              <Pencil className="size-3.5" />
                            </Button>
                          </div>
                        ) : (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="text-muted-foreground block text-right text-xs">—</span>
                            </TooltipTrigger>
                            <TooltipContent>{t('hr.register.uopOnly')}</TooltipContent>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}
